/**
 * AssetDetailPage — IVT Hub Asset Detail (`/ivt/assets/:assetId`), Figma re-design M2.
 *
 * Left = collapsible AssetNavTree (Simplified: Industry→Asset / Topic→Asset;
 * Structured: +Sales Area / Use Case). Right = Details card (AssetDetailHeader) +
 * Information (AssetInfoPanel: 8-Element ⇄ KB + Related). (No breadcrumb — the left
 * tree + the top-nav Assets tab cover the back path.)
 *
 * The nav tree needs the full asset list (same ListAssets({}) as Overview), so we
 * reuse useAssetsData. The selected asset (by :assetId) resolves from that list for
 * the tree + a summary fallback. The right side (header + Information panel) reads
 * ONE shared GetAsset detail (useAssetDetail) — GetAsset is the authoritative
 * per-asset read (the list summary can lag, e.g. sfdcTag), and both read/reload it
 * together so any edit refreshes the whole right side consistently.
 *
 * NEW page — does NOT touch NewAssetsPage. M2 first slice: right side + Simplified
 * tree (no backend-field dependency). Structured tree is a follow-up.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Spinner } from '@amzn/awsui-components-react';
import { useAssetsData } from './hooks/useAssetsData';
import { useAssetDetail } from './hooks/useAssetDetail';
import AssetNavTree from './components/AssetNavTree';
import { useStorylineTreeProps } from './hooks/useStorylineTreeProps';
import { saveAssetsView } from './components/AssetsViewSwitch';
import CollapsibleTreePanel from './components/CollapsibleTreePanel';
import AssetDetailHeader from './components/AssetDetailHeader';
import AssetInfoPanel from './components/AssetInfoPanel';
import './AssetDetailPage.css';

export default function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const { assets, industries, loading, error, reload } = useAssetsData();
  // Shared per-asset detail (GetAsset) for the whole right side.
  const { detail, loading: detailLoading, error: detailError, reload: reloadDetail } =
    useAssetDetail(assetId ?? null);

  // After a header save, reload BOTH the GetAsset detail (right side) AND the
  // ListAssets list (left nav tree). Origin (Mark/Unmark as NEW) flips the tree's
  // "New" tag, which reads from the LIST summary — reloading only the detail would
  // leave the tree stale. Harmless for the other saves (sfdcTag) too.
  // Forces the UC arsenal (Related) to refetch GetUcFull. Its own effect keys off
  // industry/solutionMapId, which a rename doesn't change — but the card renders this
  // asset's name, so without the bump the title shows the new name while the card
  // below still shows the old one.
  const [arsenalRefreshKey, setArsenalRefreshKey] = useState(0);
  const handleSaved = useCallback(() => {
    reloadDetail();
    reload();
    setArsenalRefreshKey((k) => k + 1);
  }, [reloadDetail, reload]);

  const all = useMemo(() => assets ?? [], [assets]);
  const selected = useMemo(
    () => (assetId ? all.find((a) => a.assetId === assetId) ?? null : null),
    [all, assetId],
  );

  // All Storyline-Menu tree wiring in one place (the tree mode is a shared store, so
  // this page must supply the skeleton or the tree collapses to all-Unassigned).
  const storylineTree = useStorylineTreeProps({ assets: all, industries, reloadAssets: reload });

  return (
    <div className="ivt-assetd-page">
      {loading && (
        <div className="ivt-assetd-page__loading">
          <Spinner size="large" />
          <span>加载 Asset…</span>
        </div>
      )}

      {!loading && error && (
        <Alert type="error" header="无法加载 Assets" action={<Button onClick={reload}>重试</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Breadcrumb removed: the left category tree + the top nav "Assets" tab
              both return to the Overview, and the asset name is already the header
              title — so the "All Assets › {name}" bar was redundant. */}
          <div className="ivt-assetd-shell">
            <CollapsibleTreePanel>
              <AssetNavTree
                assets={all}
                selectedAssetId={assetId ?? null}
                onSectionClick={(view) => {
                  // Persist the target view, then go to the Overview (which seeds its
                  // live view from storage on mount → lands on Industry/Topic view).
                  saveAssetsView(view);
                  navigate('/ivt/assets');
                }}
                onUseCaseClick={(id) => navigate(`/ivt/use-cases/${id}`)}
                // Storyline Menu mode: full wiring from the shared hook (skeleton,
                // industry universe, node nav, Create `+`).
                {...storylineTree.treeProps}
              />
            </CollapsibleTreePanel>
            <div className="ivt-assetd-right">
              {selected ? (
                <>
                  <AssetDetailHeader asset={selected} detail={detail} onSaved={handleSaved} />
                  <AssetInfoPanel
                    asset={selected}
                    detail={detail}
                    arsenalRefreshKey={arsenalRefreshKey}
                    loading={detailLoading}
                    error={detailError}
                    reload={reloadDetail}
                  />
                </>
              ) : (
                <div className="ivt-assetd-notfound">未找到该 Asset（{assetId}）。</div>
              )}
            </div>
          </div>
          {/* Create Storyline modal (opened by the Storyline Menu tree's `+`). */}
          {storylineTree.modalNode}
        </>
      )}
    </div>
  );
}
